import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, RefreshCw } from 'lucide-react';

import {
  listMobileCollectionTransactions,
  type MobileCollectionTransaction,
} from '../api/client';

type StatusFilter = 'all' | 'unpaid' | 'paid';

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800',
  overdue: 'bg-red-100 text-red-800',
  paid_link: 'bg-green-100 text-green-800',
  paid_cash: 'bg-green-100 text-green-800',
};

function fmtNis(n: number) {
  return `₪${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(value?: string | null) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export default function PaymentLinks() {
  const { t } = useTranslation();
  const [items, setItems] = useState<MobileCollectionTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listMobileCollectionTransactions()
      .then(setItems)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const filtered = useMemo(() => {
    const isPaid = (s: string) => s === 'paid_link' || s === 'paid_cash';
    return items
      .filter((tx) => {
        if (filter === 'paid') return isPaid(tx.status);
        if (filter === 'unpaid') return !isPaid(tx.status);
        return true;
      })
      .sort((a, b) => new Date(b.last_activity_at).getTime() - new Date(a.last_activity_at).getTime());
  }, [items, filter]);

  const isPaid = (tx: MobileCollectionTransaction) => tx.status === 'paid_link' || tx.status === 'paid_cash';

  const copyLink = async (tx: MobileCollectionTransaction) => {
    if (!tx.payment_link_url) return;
    try {
      await navigator.clipboard.writeText(tx.payment_link_url);
      setCopiedId(tx.id);
      setError(null);
      window.setTimeout(() => setCopiedId((current) => (current === tx.id ? null : current)), 2000);
    } catch {
      setError(t('paymentLinksPage.copyFailed', 'Unable to copy the payment link.'));
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-gray-900">{t('paymentLinksPage.title', 'Payment Links')}</h1>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          {t('common.refresh', 'Refresh')}
        </button>
      </div>
      <p className="text-gray-500 mb-4">
        {t('paymentLinksPage.subtitle', 'All sent payment links — see payment and reminder details, and copy any available link.')}
      </p>

      <div className="flex gap-2 mb-4">
        {(['all', 'unpaid', 'paid'] as StatusFilter[]).map((key) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-3 py-1.5 text-sm rounded-lg border ${
              filter === key ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t(`paymentLinksPage.filter.${key}`, key)}
          </button>
        ))}
      </div>

      {error ? <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div> : null}

      {loading ? (
        <div className="text-gray-500 py-12 text-center">{t('common.loading', 'Loading…')}</div>
      ) : filtered.length === 0 ? (
        <div className="text-gray-400 py-12 text-center">{t('paymentLinksPage.empty', 'No payment links yet.')}</div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-xl">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left font-medium">{t('paymentLinksPage.col.customer', 'Customer')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('paymentLinksPage.col.amount', 'Amount')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('paymentLinksPage.col.status', 'Status')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('paymentLinksPage.col.paidAt', 'Paid')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('paymentLinksPage.col.lastReminder', 'Last reminder')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('paymentLinksPage.col.reminders', 'Reminders')}</th>
                <th className="px-4 py-3 text-right font-medium">{t('paymentLinksPage.col.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filtered.map((tx) => {
                const paid = isPaid(tx);
                const copied = copiedId === tx.id;
                return (
                  <tr key={tx.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{tx.customer_name}</div>
                      <div className="text-gray-400 text-xs">{tx.customer_phone}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-900">{fmtNis(tx.amount_ils)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[tx.status] || 'bg-gray-100 text-gray-700'}`}>
                        {t(`paymentLinksPage.status.${tx.status}`, tx.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{paid ? fmtDate(tx.paid_at) : '—'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {tx.last_reminder_at ? (
                        <div>
                          <div>{fmtDate(tx.last_reminder_at)}</div>
                          {tx.last_reminder_channel ? (
                            <div className="text-xs text-gray-400 uppercase">{tx.last_reminder_channel}</div>
                          ) : null}
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {paid ? (
                        <span className="text-gray-400 text-xs">—</span>
                      ) : tx.reminders_stopped ? (
                        <span className="text-red-600 text-xs font-medium">{t('paymentLinksPage.stopped', 'Stopped')}</span>
                      ) : tx.reminders_enabled ? (
                        <span className="text-green-700 text-xs font-medium">
                          {t('paymentLinksPage.on', 'On')}
                          {tx.next_reminder_at ? ` · ${t('paymentLinksPage.next', 'next')} ${fmtDate(tx.next_reminder_at)}` : ''}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">{t('paymentLinksPage.off', 'Off')}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end">
                        {tx.payment_link_url ? (
                          <button
                            type="button"
                            onClick={() => copyLink(tx)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                          >
                            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                            {copied
                              ? t('paymentLinksPage.copied', 'Copied')
                              : t('paymentLinksPage.copyLink', 'Copy Link')}
                          </button>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
